import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  List,
  ListItem,
  ListItemText,
  Box,
  Chip,
  IconButton,
  Button
} from '@mui/material';
import {
  Restaurant as RecipeIcon,
  ArrowForward as ArrowIcon,
  Timer as TimerIcon,
  Person as PersonIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import AddRecipeDialog from '../../Recipes/AddRecipeDialog';

const safeAge = (ts) => {
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return formatDistanceToNow(d, { addSuffix: true });
  } catch { return ''; }
};

const RecipesWidget = ({ recipes, loading, onRecipeClick, onRecipeAdded }) => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const handleRecipeAdded = () => {
    if (onRecipeAdded) {
      onRecipeAdded();
    }
  };
  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          avatar={<RecipeIcon color="primary" />}
          title="Family Recipes"
        />
        <CardContent sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!recipes || recipes.length === 0) {
    return (
      <>
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <CardHeader
            avatar={<RecipeIcon color="primary" />}
            title="Family Recipes"
            action={
              <IconButton component={Link} to="/recipes">
                <ArrowIcon />
              </IconButton>
            }
          />
          <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No recipes yet. Share your family's favorite recipes!
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
            >
              Add Recipe
            </Button>
          </CardContent>
        </Card>
        <AddRecipeDialog
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          onRecipeAdded={handleRecipeAdded}
        />
      </>
    );
  }

  return (
    <>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          avatar={<RecipeIcon color="primary" />}
          title="Family Recipes"
          subheader="Delicious family favorites"
          action={
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => setAddDialogOpen(true)}
                title="Add recipe"
                color="primary"
              >
                <AddIcon />
              </IconButton>
              <IconButton component={Link} to="/recipes" title="View all">
                <ArrowIcon />
              </IconButton>
            </Box>
          }
        />
      <CardContent sx={{ pt: 0 }}>
        <List dense>
          {recipes.map((recipe) => (
            <ListItem
              key={recipe.id}
              onClick={onRecipeClick ? () => onRecipeClick(recipe) : undefined}
              sx={{
                borderLeft: 3,
                borderColor: 'warning.main',
                borderRadius: 1,
                mb: 1,
                bgcolor: 'background.default',
                cursor: onRecipeClick ? 'pointer' : 'default',
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {recipe.title || recipe.name}
                    </Typography>
                    {recipe.category && (
                      <Chip
                        label={recipe.category}
                        size="small"
                        color="warning"
                        sx={{ height: 18, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Box>
                    {recipe.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {recipe.description.length > 80
                          ? `${recipe.description.substring(0, 80)}...`
                          : recipe.description}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {recipe.prepTime && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TimerIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                          <Typography variant="caption" color="text.disabled">
                            {recipe.prepTime} min
                          </Typography>
                        </Box>
                      )}
                      {recipe.servings && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PersonIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                          <Typography variant="caption" color="text.disabled">
                            Serves {recipe.servings}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    {recipe.createdBy && (
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                        Shared by {recipe.createdBy}
                        {recipe.createdAt && ` • ${safeAge(recipe.createdAt)}`}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </CardContent>
      </Card>
      <AddRecipeDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onRecipeAdded={handleRecipeAdded}
      />
    </>
  );
};

export default RecipesWidget;
